/**
 * Point every relative API request at the real server, on a native surface only.
 *
 * WHY THIS EXISTS AT ALL, because the honest answer is "a lot of code was written assuming one
 * thing and then shipped somewhere that thing is not true". 407 call sites across 85 files fetch
 * bare paths like `/api/billing/play/config`. On the web that is correct and deliberate: the page
 * is served from nuvrahq.com and vercel.json rewrites /api/* to the API host, so a relative path
 * is same-origin and cheap.
 *
 * Inside the Android shell there is no Vercel. Capacitor serves the bundle from https://localhost
 * and answers any path it does not recognise with index.html and HTTP 200. A relative /api request
 * therefore returns an HTML page with a success status, and the caller fails on
 * `Unexpected token '<', "<!doctype "... is not valid JSON`. It looks like a server fault and is
 * not one; the request never left the phone.
 *
 * Baking the origin into the bundle (VITE_API_URL) fixed only the files that already read it.
 * Rewriting 407 call sites by hand would fix today and not tomorrow: the 408th is written next
 * week by someone who has no reason to know any of this. So the rule lives in one place, applies
 * to every caller that exists or ever will, and the call sites stay written the obvious way.
 *
 * WHAT IT DOES NOT DO, deliberately:
 *   - nothing on the web. The patch is not installed at all there, so the browser bundle behaves
 *     exactly as it did before this file existed.
 *   - nothing to absolute URLs. Supabase, Google, a customer's own host and every asset request
 *     are passed through untouched.
 *   - nothing to a path that is not /api. Capacitor's own asset requests must keep resolving to
 *     the local server or the app does not load.
 *   - no credential handling, no header rewriting, no retry, no error swallowing. It changes a URL
 *     and gets out of the way, so a failure still fails and still looks like what it is.
 */

import { isNative } from "./platform";

declare const __API_URL__: string | undefined;

/** The API origin baked at build time, normalised without a trailing slash. */
export function configuredApiOrigin(): string {
  const raw = typeof __API_URL__ === "string" ? __API_URL__ : "";
  return raw.replace(/\/+$/, "");
}

/**
 * Decide the URL a request should actually go to.
 *
 * Pure, and exported separately from the patch, so every branch can be proven without touching a
 * global or a network. Returns the input unchanged whenever the rule does not apply, which is the
 * common case.
 */
export function resolveApiUrl(input: string, origin: string, localOrigin?: string): string {
  if (!origin) return input;

  // Root-relative, the shape 400-odd call sites are written in. "/apixyz" is not an API path, and
  // "api/..." relative to the current directory is not one either: a looser test would redirect
  // both.
  if (input.startsWith("/api/") || input === "/api") return `${origin}${input}`;

  // Already absolute against the shell's own origin, which is what `new URL(path, location.origin)`
  // produces. Same request, same mistake, one step further along, so the same rule has to reach it.
  const here = localOrigin ?? (typeof location === "undefined" ? "" : location.origin);
  if (here && input.startsWith(`${here}/api/`)) {
    return `${origin}${input.slice(here.length)}`;
  }

  return input;
}

let installed = false;

/**
 * Install the rewrite. Idempotent, and a no-op on the web and wherever no origin was baked in.
 *
 * Returns true when the patch was actually installed, so a caller can report the real state rather
 * than assuming. A native build with no origin baked in is a build that cannot reach its server at
 * all, which is worth knowing about rather than papering over.
 */
export function installNativeApiOrigin(): boolean {
  if (installed) return true;
  if (!isNative()) return false;

  const origin = configuredApiOrigin();
  if (!origin) return false;

  const original = globalThis.fetch;
  if (typeof original !== "function") return false;

  globalThis.fetch = function nuvraFetch(input: RequestInfo | URL, init?: RequestInit) {
    // A string or URL is rewritten directly. A Request carries method, headers, body and mode, so
    // it is rebuilt around the new URL rather than having its href reassigned, which is not
    // allowed.
    if (typeof input === "string") {
      return original(resolveApiUrl(input, origin), init);
    }
    if (input instanceof URL) {
      return original(resolveApiUrl(input.toString(), origin), init);
    }
    if (typeof Request !== "undefined" && input instanceof Request) {
      const rewritten = resolveApiUrl(input.url, origin);
      return rewritten === input.url ? original(input, init) : original(new Request(rewritten, input), init);
    }
    return original(input as RequestInfo, init);
  } as typeof fetch;

  installed = true;
  return true;
}

/** Test seam. Never called by the product. */
export function __resetApiOriginForTests(): void {
  installed = false;
}
